import React, { useState, useEffect } from 'react';
import {
  Text,
  Group,
  Title,
  Badge,
  Loader,
  Stack,
  RingProgress,
  Tooltip,
  Divider,
  Progress,
} from '@mantine/core';
import {
  TrendingUp,
  Star,
  Activity,
  DollarSign,
  Clock,
  AlertCircle,
  Info,
  CheckCircle,
  Truck,
  Users,
  BarChart3,
  Zap,
  Target,
  AlertTriangle,
  ShieldCheck,
} from 'lucide-react';
import { analyticsService } from '../../services/analyticsService';

interface VendorIntelligenceDetailProps {
  vendorId: string;
}

// ---- Info Tooltip Component ----
const MetricInfoTooltip: React.FC<{
  title: string;
  db: string;
  formula: string;
  logic: string;
  direction?: 'left' | 'right';
}> = ({ title, db, formula, logic, direction = 'left' }) => (
  <div className={`relative cursor-help group/metricinfo`}>
    <Info className="h-3.5 w-3.5 text-gray-400 opacity-60 hover:opacity-100 transition-opacity" />
    <div
      className={`absolute ${direction === 'right' ? 'right-0' : 'left-0'} top-full mt-2 w-64 p-3.5 bg-gray-950 text-white rounded-2xl shadow-2xl opacity-0 invisible group-hover/metricinfo:opacity-100 group-hover/metricinfo:visible transition-all duration-300 z-50 text-[10px] normal-case leading-relaxed font-semibold text-left`}
    >
      <p className="font-bold text-blue-400 mb-1.5">{title}</p>
      <p className="mb-1"><span className="text-gray-400">Database:</span> {db}</p>
      <p className="mb-1"><span className="text-gray-400">Formula:</span> <code className="bg-gray-800 px-1 py-0.5 rounded">{formula}</code></p>
      <p><span className="text-gray-400">Logic:</span> {logic}</p>
    </div>
  </div>
);

// ---- Metric Row ----
const MetricRow: React.FC<{
  label: string;
  value: any;
  icon: React.ReactNode;
  iconColor: string;
  tooltipTitle: string;
  db: string;
  formula: string;
  logic: string;
  highlight?: boolean;
  direction?: 'left' | 'right';
}> = ({ label, value, icon, iconColor, tooltipTitle, db, formula, logic, highlight, direction }) => {
  const isNull = value === null || value === undefined;
  const isZero = value === '0%' || value === '0s' || value === 0;
  return (
    <Group justify="space-between" py={6} className="border-b border-gray-100/60 dark:border-gray-800/40 last:border-0">
      <Group gap="xs">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${iconColor}`}>
          {icon}
        </div>
        <Group gap={4}>
          <Text size="xs" fw={600} className="text-gray-600 dark:text-gray-300">{label}</Text>
          <MetricInfoTooltip title={tooltipTitle} db={db} formula={formula} logic={logic} direction={direction} />
        </Group>
      </Group>
      <div>
        {isNull ? (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500">N/A</span>
        ) : isZero ? (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border border-amber-200/50 dark:border-amber-800/30">
            {value} ⚠
          </span>
        ) : (
          <span className={`text-sm font-extrabold ${highlight ? 'text-blue-600 dark:text-blue-400' : 'text-gray-800 dark:text-gray-100'}`}>{value}</span>
        )}
      </div>
    </Group>
  );
};

// ---- Card Shell ----
const MetricCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  iconBg: string;
  children: React.ReactNode;
  badge?: React.ReactNode;
}> = ({ title, icon, iconBg, children, badge }) => (
  <div className="bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800/60 rounded-2xl p-5 shadow-sm h-full flex flex-col">
    <Group mb="md" justify="space-between">
      <Group gap="sm">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${iconBg}`}>
          {icon}
        </div>
        <Text fw={700} size="sm" className="dark:text-white">{title}</Text>
      </Group>
      {badge}
    </Group>
    <div className="flex-1">{children}</div>
  </div>
);

const getConfidenceColor = (conf: string) => {
  switch (conf) {
    case 'HIGH': return 'teal';
    case 'MEDIUM': return 'blue';
    case 'LOW': return 'red';
    default: return 'gray';
  }
};

const VendorIntelligenceDetail: React.FC<VendorIntelligenceDetailProps> = ({ vendorId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!vendorId) return;
      try {
        setLoading(true);
        const intelligence = await analyticsService.getDetailedVendorIntelligence(vendorId);
        setData(intelligence);
        setError(null);
      } catch (err: any) {
        console.error('Failed to fetch detailed intelligence:', err);
        setError('Could not fetch the intelligence profile for this vendor.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [vendorId]);

  if (loading) {
    return (
      <div className="h-80 flex flex-col items-center justify-center gap-3">
        <Loader size="lg" variant="dots" color="blue" />
        <Text size="sm" c="dimmed">Analyzing behavioral stream...</Text>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-5 rounded-2xl border border-red-200 dark:border-red-900/40 bg-red-50/50 dark:bg-red-900/10 flex items-start gap-3">
        <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
        <div>
          <Text size="sm" fw={700} className="text-red-700 dark:text-red-400">Intelligence Gap</Text>
          <Text size="xs" c="dimmed">{error || 'No data available for this vendor.'}</Text>
        </div>
      </div>
    );
  }

  const { vendor, performance, quality, utilization, business, insights, data_confidence } = data;

  const reliabilityScore = (quality?.reliability_score ?? 0) * 100;
  const acceptanceRate = (performance?.acceptance_rate ?? 0) * 100;
  const cancellationRate = (performance?.cancellation_rate ?? 0) * 100;
  const conversionRate = (performance?.conversion_rate ?? 0) * 100;  const repeatRate = (quality?.repeat_customer_rate ?? 0) * 100;
  const fleetUsage = (utilization?.fleet_utilization ?? 0) * 100;
  const availSync = (utilization?.availability_consistency ?? 0) * 100;

  const category = vendor?.category || 'ambulance';
  const isDoctor = category === 'doctor';
  const isStore = category === 'medical_store';

  // Dynamic Labels
  const totalOrdersLabel = isDoctor ? "Total Appointments" : isStore ? "Total Orders" : "Total Bookings";
  const completedLabel = isDoctor ? "Completed Consultations" : isStore ? "Completed Orders" : "Completed Bookings";
  const acceptanceLabel = isDoctor ? "Appointment Confirmation" : isStore ? "Fulfillment Rate" : "Acceptance Rate";
  const avgResponseLabel = isDoctor ? "Avg Consult Time" : isStore ? "Avg Processing Time" : "Avg Response Time";
  const repeatLabel = isDoctor ? "Repeat Patients" : "Repeat Customers";
  const ticketSizeLabel = isDoctor ? "Avg Consultation Fee" : isStore ? "Avg Order Value" : "Avg Ticket Size";
  const cancelRiskLabel = isDoctor ? "No-Show Risk" : "Cancellation Risk";
  const badgeLabel = isDoctor ? "appointments" : isStore ? "orders" : "bookings";

  // Dynamic Tooltips DB references
  const totalOrdersDb = isDoctor ? "MySQL: ClinicAppointments" : isStore ? "MySQL: MedicineOrders" : "MySQL: AmbulanceBookings";
  const totalOrdersFormula = isDoctor ? "COUNT(appointmentId)" : isStore ? "COUNT(orderId)" : "COUNT(bookingId)";
  const totalOrdersLogic = isDoctor ? "All appointments booked with this clinic, regardless of status." : isStore ? "All medicine orders received by this store." : "All booking requests received for this vendor.";

  const completedDb = isDoctor ? "MySQL: ClinicAppointments (status = COMPLETED)" : isStore ? "MySQL: MedicineOrders (status = DELIVERED)" : "BigQuery: vendor_features_daily";
  const completedFormula = isDoctor ? "COUNT(entries WHERE status = 'COMPLETED')" : isStore ? "COUNT(entries WHERE status = 'DELIVERED')" : "orders column (= completed bookings)";
  const completedLogic = isDoctor ? "Appointments successfully completed and checked out." : isStore ? "Orders successfully delivered and paid." : "Bookings that reached a final completed/settled state.";

  const acceptanceDb = isDoctor ? "MySQL: ClinicAppointments" : isStore ? "MySQL: MedicineOrders" : "BigQuery: events (Bronze) or vendor_features_daily (Silver)";
  const acceptanceFormula = isDoctor ? "Confirmed / Total Booked" : isStore ? "Flipped to FULFILLED / Total" : "ACCEPTED_events / TOTAL_BOOKING_ATTEMPT_events";
  const acceptanceLogic = isDoctor ? "Percentage of clinic appointments successfully confirmed by the doctor." : isStore ? "Ratio of store orders accepted and processed." : "Ratio of ambulance booking requests accepted by the agency.";

  const conversionDb = isDoctor ? "BigQuery: events" : isStore ? "BigQuery: events" : "BigQuery: events";
  const conversionFormula = isDoctor ? "Appointments / Profile Views" : isStore ? "Orders / Profile Views" : "COMPLETED_events / TOTAL_VIEW_events";
  const conversionLogic = isDoctor ? "Percentage of users who viewed this doctor clinic and completed an appointment booking." : isStore ? "Percentage of users who viewed this store and successfully placed an order." : "Percentage of users who viewed this vendor profile and ultimately completed a booking.";

  const responseDb = isDoctor ? "MySQL: ClinicAppointments" : isStore ? "MySQL: MedicineOrders" : "BigQuery: events";
  const responseFormula = isDoctor ? "AVG(serviceCompletedAt - createdAt)" : isStore ? "AVG(orderPackedAt - createdAt)" : "AVG(dwell_time_sec) WHERE event = AMBULANCE_BOOKING_ACCEPTED";
  const responseLogic = isDoctor ? "Average duration of a completed consultation session." : isStore ? "Average time taken to prepare and pack a medicine order." : "Average time taken by the ambulance pilot to accept a live dispatch invite.";

  const ratingDb = isDoctor ? "MySQL: ClinicFeedbacks" : isStore ? "MySQL: StoreFeedbacks" : "BigQuery: Bronze fallback";
  const ratingFormula = "AVG(rating)";
  const ratingLogic = isDoctor ? "Average patient feedback rating score left for clinic consultations." : isStore ? "Average customer feedback rating score left for store orders." : "Avg user review score. Falls back to static 4.5 in Bronze mode.";

  const repeatDb = isDoctor ? "MySQL: ClinicAppointments" : isStore ? "MySQL: MedicineOrders" : "BigQuery: Bronze fallback";
  const repeatFormula = "(Total - Unique Patients) / Total";
  const repeatLogic = isDoctor ? "Percentage of bookings driven by returning/loyal patients." : isStore ? "Percentage of orders placed by repeat customers." : "% of bookings from returning users. Bronze fallback uses static 15%.";

  const ticketDb = isDoctor ? "MySQL: ClinicAppointments" : isStore ? "MySQL: MedicineOrders" : "BigQuery: Bronze fallback";
  const ticketFormula = isDoctor ? "AVG(consultationFee)" : isStore ? "AVG(payableAmount)" : "avg_booking_value";
  const ticketLogic = isDoctor ? "Average fee charged per consultation session." : isStore ? "Average invoice value per medicine order." : "Average payment value per completed booking.";

  const revenueDb = isDoctor ? "MySQL: ClinicAppointments" : isStore ? "MySQL: MedicineOrders" : "BigQuery: Bronze/Silver";
  const revenueFormula = isDoctor ? "SUM(consultationFee)" : isStore ? "SUM(payableAmount)" : "orders * avg_booking_value";
  const revenueLogic = isDoctor ? "Total estimated consultation value generated by the clinic." : isStore ? "Total estimated gross sales value generated by the store." : "Total estimated platform GMV from this vendor.";

  const demandGapText = isDoctor ? "This doctor clinic captures " : isStore ? "This medical store captures " : "This vendor captures ";
  const demandGapActionText = isDoctor ? "booking stage — may need pricing or consult-time improvements." : isStore ? "checkout stage — may need stock availability or delivery speed improvements." : "booking stage — may need pricing or response-time improvements.";

  return (
    <div className="space-y-4 animate-in fade-in duration-500">

      {/* ── LOW DATA WARNING ── */}
      {data_confidence.level === 'LOW' && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50/60 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800/40">
          <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <Text size="xs" fw={700} className="text-amber-700 dark:text-amber-400">Low Data Confidence</Text>
            <Text size="xs" c="dimmed">
              This vendor has little to no activity logged in BigQuery in the last 30 days. Metrics show <strong>0%</strong> because no bookings, events, or acceptance actions were recorded — not because the vendor is inactive. The <strong>Silver feature table is empty</strong> for this vendor and the <strong>Bronze event fallback returned no results</strong>.
            </Text>
          </div>
        </div>
      )}

      {/* ── TOP ROW: IDENTITY + RELIABILITY ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Identity Card */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800/60 rounded-2xl p-5 shadow-sm">
          <Group justify="space-between" align="flex-start" mb="lg">
            <Group gap="sm">
              <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
                {vendor.category === 'doctor' ? (
                  <Activity size={20} className="text-blue-600 dark:text-blue-400" />
                ) : vendor.category === 'medical_store' ? (
                  <Users size={20} className="text-blue-600 dark:text-blue-400" />
                ) : (
                  <Truck size={20} className="text-blue-600 dark:text-blue-400" />
                )}
              </div>
              <Stack gap={2}>
                <Title order={4} className="dark:text-white">{vendor.agency_name}</Title>
                <Text size="xs" c="dimmed" fw={500}>
                  {vendor.city} · {vendor.category === 'doctor' ? 'Clinic & Doctor' : vendor.category === 'medical_store' ? 'Medical Store' : 'Ambulance Provider'}
                </Text>
              </Stack>
            </Group>
            <Group gap="xs">
              {insights.is_high_performer && (
                <Badge variant="filled" color="teal" size="sm" leftSection={<Zap size={10} />}>High Performer</Badge>
              )}
              {insights.is_at_risk && (
                <Badge variant="filled" color="red" size="sm" leftSection={<AlertCircle size={10} />}>At Risk</Badge>
              )}
              {insights.is_underutilized && (
                <Badge variant="filled" color="yellow" size="sm" leftSection={<Activity size={10} />}>Underutilized</Badge>
              )}
              <Tooltip label={data_confidence.reason} position="bottom-end">
                <Badge
                  variant="dot"
                  color={getConfidenceColor(data_confidence.level)}
                  className="cursor-help"
                  size="sm"
                >
                  Confidence: {data_confidence.level}
                </Badge>
              </Tooltip>
            </Group>
          </Group>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50/50 dark:bg-slate-800/40 rounded-xl p-3">
              <Text size="10px" fw={800} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {vendor.category === 'doctor' ? 'Doctor Name' : 'Owner'}
              </Text>
              <Text size="sm" fw={700} className="dark:text-white mt-0.5">{vendor.owner_name}</Text>
            </div>
            <div className="bg-gray-50/50 dark:bg-slate-800/40 rounded-xl p-3">
              <Text size="10px" fw={800} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {vendor.category === 'doctor' ? 'Experience' : vendor.category === 'medical_store' ? 'Type' : 'Fleet Size'}
              </Text>
              <Text size="sm" fw={700} className="dark:text-white mt-0.5">
                {vendor.category === 'doctor' ? `${vendor.num_of_ambulances} Years` : vendor.category === 'medical_store' ? 'Pharmacy' : `${vendor.num_of_ambulances} Vehicles`}
              </Text>
            </div>
            <div className="bg-gray-50/50 dark:bg-slate-800/40 rounded-xl p-3">
              <Text size="10px" fw={800} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Availability</Text>
              <Badge variant="light" color={vendor.is_24x7_available ? 'teal' : 'gray'} size="sm" mt={4}>
                {vendor.is_24x7_available ? '24/7 Available' : 'Limited Hours'}
              </Badge>
            </div>
          </div>
        </div>

        {/* Reliability Ring */}
        <div className="bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800/60 rounded-2xl p-5 shadow-sm flex flex-col items-center justify-center">
          <Group gap="xs" mb="md">
            <ShieldCheck size={16} className="text-blue-500" />
            <Text size="xs" fw={800} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Operational Reliability</Text>
            <MetricInfoTooltip
              title="Operational Reliability Score"
              db="BigQuery: vendor_features_daily (Silver) or events fallback (Bronze)"
              formula="(Accepted - Cancelled) / Total Requests"
              logic="Composite score measuring how consistently this vendor accepts and completes requests without cancelling. Higher is better. Falls back to hardcoded 0.95 if feature table is empty."
              direction="right"
            />
          </Group>
          <RingProgress
            size={130}
            roundCaps
            thickness={11}
            sections={[{
              value: reliabilityScore,
              color: reliabilityScore > 75 ? 'teal' : reliabilityScore > 50 ? 'blue' : 'red'
            }]}
            label={
              <Text ta="center" fw={900} size="xl" className={
                reliabilityScore > 75 ? 'text-teal-600 dark:text-teal-400' :
                reliabilityScore > 50 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600'
              }>
                {Math.round(reliabilityScore)}%
              </Text>
            }
          />
          <Text size="xs" c="dimmed" ta="center" mt="xs" maw={160}>
            Based on acceptance & cancellations over 30 days
          </Text>
        </div>
      </div>

      {/* ── METRIC CARDS ROW ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* Performance Vitals */}
        <MetricCard
          title="Performance Vitals"
          icon={<BarChart3 size={18} className="text-blue-600 dark:text-blue-400" />}
          iconBg="bg-blue-50 dark:bg-blue-950/40"
          badge={
            <Badge size="xs" variant="light" color="blue">
              {performance.total_orders ?? 0} {badgeLabel}
            </Badge>
          }
        >
          <Stack gap={0}>
            <MetricRow
              label={totalOrdersLabel} value={performance.total_orders ?? 'N/A'}
              icon={<TrendingUp size={13} />} iconColor="bg-blue-50 dark:bg-blue-900/30 text-blue-500"
              tooltipTitle={totalOrdersLabel} db={totalOrdersDb} formula={totalOrdersFormula} logic={totalOrdersLogic} />
            <MetricRow
              label={completedLabel} value={performance.completed_orders ?? 'N/A'}
              icon={<CheckCircle size={13} />} iconColor="bg-teal-50 dark:bg-teal-900/30 text-teal-500"
              tooltipTitle={completedLabel} db={completedDb} formula={completedFormula} logic={completedLogic} />
            <MetricRow
              label={acceptanceLabel} value={acceptanceRate ? `${Math.round(acceptanceRate)}%` : '0%'}
              icon={<Zap size={13} />} iconColor="bg-yellow-50 dark:bg-yellow-900/30 text-yellow-500"
              tooltipTitle={acceptanceLabel} db={acceptanceDb} formula={acceptanceFormula} logic={acceptanceLogic} />
            <MetricRow
              label="Conversion" value={conversionRate ? `${Math.round(conversionRate)}%` : '0%'}
              icon={<Target size={13} />} iconColor="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-500"
              tooltipTitle="Conversion Rate" db={conversionDb} formula={conversionFormula} logic={conversionLogic} />
            <MetricRow
              label={avgResponseLabel} value={performance.estimated_response_time ? `${performance.estimated_response_time}s` : '0s'}
              icon={<Clock size={13} />} iconColor="bg-gray-100 dark:bg-gray-800 text-gray-500"
              tooltipTitle={avgResponseLabel} db={responseDb} formula={responseFormula} logic={responseLogic} />
          </Stack>
        </MetricCard>

        {/* Quality & Trust */}
        <MetricCard
          title="Quality & Trust"
          icon={<Star size={18} className="text-yellow-500" />}
          iconBg="bg-yellow-50 dark:bg-yellow-950/40"
          badge={
            quality.avg_rating ? (
              <Badge size="xs" variant="light" color="yellow" leftSection={<Star size={10} fill="currentColor" />}>
                {quality.avg_rating}/5
              </Badge>
            ) : undefined
          }
        >
          <Stack gap={0}>
            <MetricRow
              label="Average Rating" value={quality.avg_rating ? `${quality.avg_rating}/5` : 'null/5'}
              icon={<Star size={13} />} iconColor="bg-yellow-50 dark:bg-yellow-900/30 text-yellow-500"
              tooltipTitle="Average Rating" db={ratingDb} formula={ratingFormula} logic={ratingLogic} />
            <MetricRow
              label={repeatLabel} value={repeatRate ? `${Math.round(repeatRate)}%` : '0%'}
              icon={<Users size={13} />} iconColor="bg-teal-50 dark:bg-teal-900/30 text-teal-500"
              tooltipTitle={repeatLabel} db={repeatDb} formula={repeatFormula} logic={repeatLogic} />
            <Divider
              my="sm"
              label={
                <Group gap={4}>
                  <Activity size={11} className="text-gray-400" />
                  <Text size="10px" fw={700} c="dimmed">Fulfillment & Slotting</Text>
                </Group>
              }
              labelPosition="center"
            />
            <MetricRow
              label={vendor.category === 'doctor' ? "Slot Utilization" : vendor.category === 'medical_store' ? "Order Fulfillment" : "Fleet Usage"}
              value={fleetUsage ? `${Math.round(fleetUsage)}%` : '0%'}
              icon={vendor.category === 'doctor' ? <Activity size={13} /> : vendor.category === 'medical_store' ? <Users size={13} /> : <Truck size={13} />}
              iconColor="bg-blue-50 dark:bg-blue-900/30 text-blue-500"
              tooltipTitle={vendor.category === 'doctor' ? "Slot Utilization" : vendor.category === 'medical_store' ? "Order Fulfillment" : "Fleet Utilization"}
              db={vendor.category === 'doctor' ? "BigQuery: Slot reservations" : vendor.category === 'medical_store' ? "BigQuery: Order statuses" : "BigQuery: Bronze fallback (hardcoded 85%)"}
              formula="slot_utilization_rate"
              logic={vendor.category === 'doctor' ? "Ratio of clinic time slots booked vs capacity." : vendor.category === 'medical_store' ? "Ratio of medical store orders completed vs received." : "Ratio of fleet capacity being actively used."}
            />
            <MetricRow
              label="Availability Sync" value={availSync ? `${Math.round(availSync)}%` : '0%'}
              icon={<CheckCircle size={13} />} iconColor="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-500"
              tooltipTitle="Availability Consistency" db="BigQuery: Bronze fallback (hardcoded 92%)" formula="availability_consistency" logic="How consistently the vendor keeps their availability status in sync. Bronze fallback uses static 92%." />
          </Stack>
        </MetricCard>

        {/* Business Impact */}
        <MetricCard
          title="Business Impact"
          icon={<DollarSign size={18} className="text-emerald-500" />}
          iconBg="bg-emerald-50 dark:bg-emerald-950/40"
        >
          <Stack gap={0}>
            <MetricRow
              label={ticketSizeLabel}
              value={business.avg_booking_value !== null ? `₹${Number(business.avg_booking_value).toLocaleString()}` : null}
              icon={<DollarSign size={13} />} iconColor="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-500"
              tooltipTitle={ticketSizeLabel} db={ticketDb} formula={ticketFormula} logic={ticketLogic} direction="right" />
            <MetricRow
              label="Est. Revenue"
              value={business.estimated_revenue !== null ? `₹${Number(business.estimated_revenue).toLocaleString()}` : null}
              icon={<TrendingUp size={13} />} iconColor="bg-teal-50 dark:bg-teal-900/30 text-teal-500"
              tooltipTitle="Estimated Revenue" db={revenueDb} formula={revenueFormula} logic={revenueLogic} direction="right" />
          </Stack>

          {/* Demand Gap Box */}
          <div className="mt-4 p-3.5 rounded-xl bg-blue-50/50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/30">
            <Group gap="xs" mb={6}>
              <div className="relative cursor-help group/demandinfo">
                <Info size={13} className="text-blue-500" />
                <div className="absolute right-0 top-full mt-2 w-64 p-3 bg-gray-950 text-white rounded-xl shadow-xl opacity-0 invisible group-hover/demandinfo:opacity-100 group-hover/demandinfo:visible transition-all duration-300 z-50 text-[10px] leading-relaxed font-semibold">
                  <p className="font-bold text-blue-400 mb-1">Demand Gap Analysis</p>
                  <p>Compares profile views (impressions) to actual bookings made. A low conversion rate (e.g. 0%) indicates users view the profile but don't book — signalling pricing, trust, or availability issues.</p>
                </div>
              </div>
              <Text size="xs" fw={800} className="text-blue-700 dark:text-blue-300">Demand Gap Analysis</Text>
            </Group>
            <Text size="xs" c="dimmed" style={{ lineHeight: 1.5 }}>
              {demandGapText} <strong>{Math.round(conversionRate)}%</strong> of search impressions.
              {conversionRate < 10
                ? ` ⚠ High drop-off at ${demandGapActionText}`
                : ' ✓ Strong conversion compared to vertical average.'}
            </Text>

            {/* Cancellation bar */}
            {cancellationRate > 0 && (
              <div className="mt-3">
                <Group justify="space-between" mb={4}>
                  <Text size="10px" fw={700} c="dimmed">{cancelRiskLabel}</Text>
                  <Text size="10px" fw={800} className={cancellationRate > 50 ? 'text-red-500' : 'text-orange-500'}>
                    {Math.round(cancellationRate)}%
                  </Text>
                </Group>
                <Progress
                  value={cancellationRate}
                  color={cancellationRate > 50 ? 'red' : 'orange'}
                  size="sm"
                  radius="xl"
                />
              </div>
            )}
          </div>
        </MetricCard>
      </div>
    </div>
  );
};

export default VendorIntelligenceDetail;
